import { NgOptimizedImage } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import jwtDecode from 'jwt-decode';
import { KeycloakService } from 'keycloak-angular';
import { Subscription } from 'rxjs';
import { AuthService } from '../../common/services/auth.service';
import { SnackMessageService } from '../../common/services/snackMessage.service';
import { UtilsService } from '../../common/services/utils.service';
import { StorageService } from '../../common/services/storage.service';
import { ModalErrorTierPermissionComponent } from '../../common/components/modal-error-tier-permission/modal-error-tier-permission.component';
import { CategoriesService } from '../../common/services/categories.service';
import { CategoriesStoreService } from '../../common/services/category-observable.service';
import { CategoryModel } from '../../common/models/common.model';

interface SimplifiedCompany {
  id: string;
  name: string;
  avatar?: string;
}

@Component({
  selector: 'app-multicompany-page',
  standalone: true,
  imports: [MatIcon, MatTooltip, NgOptimizedImage],
  templateUrl: './multicompany-page.component.html',
  styleUrl: './multicompany-page.component.scss'
})
export class MulticompanyPageComponent implements OnInit, OnDestroy {
  className = 'multicompany-page';
  url: string;
  allCompanies: SimplifiedCompany[];
  dataCompany: any;
  infoTierUsed = {};
  arraySubscriptions: Subscription[] = [];
  tierLimits = {};
  dataCategory: CategoryModel[];
  dataCategories = [];
  categoryFeaturedId;
  categoryPurchasedId;
  categoryMostPurchasedId;
  categorySpecialId;
  categorySuggestedId;
  remainingDays = {
    days: 0,
    months: 0,
    years: 0
  };
  isLoading = true;

  userMainRole: string;
  width: number = window.innerWidth;

  storageService = inject(StorageService);
  matDialog = inject(MatDialog);
  utilsService = inject(UtilsService);
  snackMessageService = inject(SnackMessageService);

  constructor(
    private readonly keycloak: KeycloakService,
    private authService: AuthService,
    private router: Router,
    private categoriesService: CategoriesService,
    private categoriesStoreService: CategoriesStoreService
  ) {}

  ngOnInit() {
    const token = '';
    this.keycloak
      .getToken()
      .then((token) => {
        if (token) {
          const data: any = jwtDecode(token);
          this.allCompanies = data.companies;
          if (this.allCompanies.length === 1) {
            this.selectCompany(this.allCompanies[0].id, this.allCompanies[0].name);
          } else if (this.allCompanies.length > 1) {
            if (this.storageService.getCompanyId() !== null) {
              this.selectCompany(
                this.storageService.getCompanyId()!,
                this.allCompanies.find(
                  (company) => company.id === this.storageService.getCompanyId()
                )?.name
              );
            } else {
              this.isLoading = false;
            }
          }
        }
      })
      .catch((error) => {
        console.error('Error getting token:', error);
        this.isLoading = false;
        this.snackMessageService.readError(error);
        return null;
      });
  }

  getCompanyLimits() {
    this.arraySubscriptions.push(
      this.authService.getCompany().subscribe({
        next: (response) => {
          this.dataCompany = response.body.response;
          this.infoTierUsed = this.dataCompany.infoTierUsed;
          this.remainingDays = this.utilsService.calculateRemainingTime(
            this.dataCompany?.tier.licenseExpirationDate
          );
          this.tierLimits = Object.fromEntries(
            Object.entries(this.dataCompany.tier.limits).filter(
              ([key, value]) => typeof value === 'number'
            )
          );
          const licenseProgress = this.utilsService.checkLicenseProgress(
            this.infoTierUsed,
            this.tierLimits
          );
          const notModalExpired = this.storageService.getNoLicenseWarning();
          const notModalMaxUsage = this.storageService.getNoModalMaxUsage();
          if (
            notModalExpired === false &&
            this.remainingDays.years === 0 &&
            this.remainingDays.months === 0
          ) {
            const licenseExpiration = this.matDialog.open(ModalErrorTierPermissionComponent, {
              height: 'auto',
              width: this.width > 700 ? '400px' : '100vw',
              maxHeight: '100vh',
              maxWidth: '100vw',
              panelClass: 'custom-dialog-container',
              backdropClass: 'backdropBackground',
              disableClose: true,
              autoFocus: false,
              data: {
                errorStatus: 999,
                errorCode: 'E995',
                userMainRole: this.userMainRole,
                licenseDate: this.utilsService.formatDateWithoutTime(
                  this.dataCompany.tier.licenseExpirationDate
                )
              }
            });
            licenseExpiration.afterClosed().subscribe((result) => {
              if (result.event === 'close') {
                if (licenseProgress.overLimit) {
                  this.openModalOverLimits(notModalExpired);
                } else if (licenseProgress.nearLimit) {
                  this.openModalNearLimits(notModalMaxUsage);
                }
              }
            });
          } else {
            if (licenseProgress.overLimit) {
              this.openModalOverLimits(notModalMaxUsage);
            } else if (licenseProgress.nearLimit) {
              this.openModalNearLimits(notModalMaxUsage);
            }
          }
        },
        error: (error) => {
          this.snackMessageService.readError(error);
        }
      })
    );
  }

  openModalOverLimits(notModalMaxUsage: boolean) {
    if (this.userMainRole !== 'role_company_admin' && notModalMaxUsage === false) {
      this.matDialog.open(ModalErrorTierPermissionComponent, {
        height: 'auto',
        width: this.width > 700 ? '400px' : '100vw',
        maxHeight: '100vh',
        maxWidth: '100vw',
        panelClass: 'custom-dialog-container',
        backdropClass: 'backdropBackground',
        disableClose: true,
        autoFocus: false,
        data: {
          errorStatus: 999,
          errorCode: 'E998',
          userMainRole: this.userMainRole,
          licenseDate: this.utilsService.formatDateWithoutTime(
            this.dataCompany.tier.licenseExpirationDate
          )
        }
      });
    }
    if (this.userMainRole === 'role_company_admin' && notModalMaxUsage === false) {
      this.matDialog.open(ModalErrorTierPermissionComponent, {
        height: 'auto',
        width: this.width > 700 ? '400px' : '100vw',
        maxHeight: '100vh',
        maxWidth: '100vw',
        panelClass: 'custom-dialog-container',
        backdropClass: 'backdropBackground',
        disableClose: true,
        autoFocus: false,
        data: {
          errorStatus: 999,
          errorCode: 'E999',
          userMainRole: this.userMainRole,
          licenseDate: this.utilsService.formatDateWithoutTime(
            this.dataCompany.tier.licenseExpirationDate
          )
        }
      });
    }
  }

  openModalNearLimits(notModalMaxUsage: boolean) {
    if (this.userMainRole !== 'role_company_admin' && notModalMaxUsage === false) {
      this.matDialog.open(ModalErrorTierPermissionComponent, {
        height: 'auto',
        width: this.width > 700 ? '400px' : '100vw',
        maxHeight: '100vh',
        maxWidth: '100vw',
        panelClass: 'custom-dialog-container',
        backdropClass: 'backdropBackground',
        disableClose: true,
        autoFocus: false,
        data: {
          errorStatus: 999,
          errorCode: 'E996',
          userMainRole: this.userMainRole,
          licenseDate: this.utilsService.formatDateWithoutTime(
            this.dataCompany.tier.licenseExpirationDate
          )
        }
      });
    }
    if (this.userMainRole === 'role_company_admin' && notModalMaxUsage === false) {
      this.matDialog.open(ModalErrorTierPermissionComponent, {
        height: 'auto',
        width: this.width > 700 ? '400px' : '100vw',
        maxHeight: '100vh',
        maxWidth: '100vw',
        panelClass: 'custom-dialog-container',
        backdropClass: 'backdropBackground',
        disableClose: true,
        autoFocus: false,
        data: {
          errorStatus: 999,
          errorCode: 'E997',
          userMainRole: this.userMainRole,
          licenseDate: this.utilsService.formatDateWithoutTime(
            this.dataCompany.tier.licenseExpirationDate
          )
        }
      });
    }
  }

  selectCompany(companyId: string, companyName?: string) {
    this.storageService.setCompanyId(companyId);
    this.storageService.setCompanyName(companyName);
    /*    this.changelogService
      .getLastChangeLog()
      .pipe(take(1))
      .subscribe((response) => {
        this.checkChangelogVersion(response);
      });*/
    this.getCompanyLimits();
    this.storageService.removeGroup();
    this.initCategories();
    this.url = this.storageService.getUrlReload();
    if (this.url && this.url !== '/multicompany') {
      this.router.navigate([this.url]);
    } else {
      this.router.navigate(['/home']);
    }
  }
  initCategories() {
    this.arraySubscriptions.push(
      this.categoriesService.getAllCategories().subscribe({
        next: (data) => {
          let _originalData = [];
          _originalData = data;
          this.dataCategories = [];
          this.dataCategory = [];
          this.dataCategory = data;
          //filtramos por privada y habilitada al servicio llegan todas
          this.dataCategory = _originalData.map((category) => {
            category.applications = this.filterCategoryApplicationsPrivateAndEnabled(
              category.applications
            );
            return category;
          });
          this.dataCategory.forEach((category) => {
            category.applications.forEach((app) => {
              let _appCategories = [];
              let _dataTagsList = [];
              //Rellenamos las categorias de las aplicaciones solo llega id
              app.categories.forEach((cat) => {
                let _cat = _originalData.find((c) => c._id === cat);
                if (_cat !== undefined) {
                  cat = {
                    _id: _cat._id,
                    name: _cat.name,
                    icon: _cat.icon,
                    order: _cat.order,
                    type: _cat.type
                  };
                  _appCategories.push(cat);
                  //Añadimos las tags que se necesitan en las cards
                  if (cat.type === 3) {
                    if (!_dataTagsList.includes('Best Seller')) {
                      _dataTagsList.push('Best Seller');
                    }
                  }
                  if (cat.type === 2) {
                    if (!_dataTagsList.includes('In library')) {
                      _dataTagsList.push('In library');
                    }
                  }
                  if (cat.type === 4 && app.price !== undefined && app.price.length > 0) {
                    if (app.price[0].value === 0) {
                      if (!_dataTagsList.includes('Free')) {
                        _dataTagsList.push('Free');
                      }
                    }
                  }
                }
              });
              if (_appCategories.length > 0) {
                app.categories = _appCategories;
              }
              if (_dataTagsList.length > 0) {
                app.dataTagList = _dataTagsList;
              }
            });
            //Con las categorías actualizadas seteamos los observables de las especiales
            this.updateCategoriesSpecials(category);
            this.dataCategories.push(category);
          });
          this.categoriesStoreService.setCategoryList(this.dataCategories);
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          if (error.error.response !== undefined) {
            this.snackMessageService.readError(error);
          } else {
            this.snackMessageService.sendMessage(error.error);
          }
        }
      })
    );
  }
  // Antes filtraba además por !isPublic. La visibilidad pública se gestiona
  // ahora vía landings; aquí solo importa que la app esté habilitada.
  filterCategoryApplicationsPrivateAndEnabled(categoryApplicationsList) {
    let _enabledApps = [];
    categoryApplicationsList.forEach((application) => {
      if (application.enabled) {
        _enabledApps.push(application);
      }
    });
    return _enabledApps;
  }

  updateCategoriesSpecials(category) {
    switch (category.type) {
      case 1:
        this.categoriesStoreService.setFeaturedCategoryId(category._id);
        this.categoriesStoreService.setFeaturedCategory(category);
        this.categoryFeaturedId = category._id;
        break;
      case 2:
        this.categoriesStoreService.setPurchasedCategoryId(category._id);
        this.categoriesStoreService.setPurchasedCategory(category);
        this.categoryPurchasedId = category._id;
        break;
      case 3:
        this.categoriesStoreService.setMostPurchasedCategoryId(category._id);
        this.categoriesStoreService.setMostPurchasedCategory(category);
        this.categoryMostPurchasedId = category._id;
        break;
      case 4:
        this.categoriesStoreService.setSpecialCategoryId(category._id);
        this.categoriesStoreService.setSpecialCategory(category);
        this.categorySpecialId = category._id;
        break;
      case 5:
        this.categoriesStoreService.setSuggestedCategoryId(category._id);
        this.categoriesStoreService.setSuggestedCategory(category);
        this.categorySuggestedId = category._id;
        break;
      default:
        //standard
        break;
    }
  }

  ngOnDestroy() {}
}
